import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import { useUserContext } from '../../contexts/UserContext';
import { useTokenExpiry } from '../../contexts/TokenExpiryManager';

import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { Alert, Snackbar, CircularProgress } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';

const SignIn = () => {
    const navigate = useNavigate();
    const { login } = useUserContext();
    const { setLoginTimestamp } = useTokenExpiry();

    const [formValues, setFormValues] = useState({ username: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [errors, setErrors] = useState({});
    const [signInSuccess, setSignInSuccess] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues(prev => ({ ...prev, [name]: value }));
        if (value) setErrors(prev => ({ ...prev, [name]: false }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formValues.username) newErrors.username = true;
        if (!formValues.password) newErrors.password = true;
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleClickShowPassword = () => setShowPassword(!showPassword);

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrorMessage('');

        if (!validate()) {
            setErrorMessage('Please fill in all the required fields.');
            return;
        }

        setLoading(true);
        const payload = { username: formValues.username, password: formValues.password };
        axios.post(
            '/auth/token/',
            payload,
        )
            .then(response => {
                const { access_token } = response.data;
                const decoded = jwtDecode(access_token);
                const username = decoded.preferred_username || decoded.username || decoded.email || '';
                login(access_token, username);
                setLoginTimestamp();
                setSignInSuccess(true);
                navigate('/');
            })
            .catch(() => {
                setErrorMessage('Invalid username or password. Please try again.');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleCloseSnackbar = () => setSignInSuccess(false);

    return (
        <>
            <CssBaseline />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    minHeight: '91vh',
                    height: 'auto',
                    background: 'linear-gradient(135deg, #5871b7 0%, #31bebe 100%)',
                    overflow: 'auto',
                }}
            >

                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 4,
                        overflow: 'auto',
                    }}
                >
                    <Fade in timeout={800}>
                        <Card sx={{ width: { xs: '90%', sm: '70%', md: '60%' }, borderRadius: 4, boxShadow: 6, p: 3, background: 'rgba(255,255,255,0.95)', maxHeight: { xs: '95vh', sm: '90vh' }, overflowY: 'auto' }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                {/* MetaBuild Logo on top */}
                                <Box sx={{ width: 120, mb: 2 }}>
                                    <img src="/images/metabuild_logo.svg" alt="MetaBuild Logo" style={{ width: '100%', display: 'block', margin: '0 auto' }} />
                                </Box>
                                {/* Login Icon in Avatar */}
                                <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', boxShadow: 3, width: 56, height: 56 }}>
                                    <LoginIcon fontSize="large" />
                                </Avatar>
                                <Typography component="h1" variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Sign In</Typography>
                                <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    {['username', 'password'].map((field) => (
                                        <TextField
                                            key={field}
                                            onChange={handleChange}
                                            color="primary"
                                            variant="outlined"
                                            margin="normal"
                                            required
                                            fullWidth
                                            id={field}
                                            type={field === 'password' && !showPassword ? 'password' : 'text'}
                                            label={field.charAt(0).toUpperCase() + field.slice(1)}
                                            name={field}
                                            autoComplete={field}
                                            autoFocus={field === 'username'}
                                            error={!!errors[field]}
                                            sx={{ width: '80%' }}
                                            InputProps={field === 'password' ? {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                        >
                                                            {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            } : null}
                                        />
                                    ))}
                                    <Box sx={{ mt: 3, mb: 2, width: '80%' }}>
                                        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress color="primary" /></Box>}
                                        <Button
                                            sx={{
                                                color: 'white',
                                                mt: 2,
                                                width: '100%',
                                                background: 'linear-gradient(to right, #5871b7, #31bebe)',
                                                boxShadow: 3,
                                                fontWeight: 600,
                                                letterSpacing: 1,
                                                '&:hover': {
                                                    background: 'linear-gradient(to right, #5871b7, #31bebe)',
                                                },
                                            }}
                                            type="submit"
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                        >
                                            <Typography>SIGN IN</Typography>
                                        </Button>
                                    </Box>
                                </Box>
                                {errorMessage && <Alert severity="error" sx={{ width: '80%', mt: 2 }}>{errorMessage}</Alert>}
                                <Divider sx={{ width: '80%', my: 2 }} />
                                {/* Signup link removed */}
                            </CardContent>
                        </Card>
                    </Fade>
                    <Snackbar open={signInSuccess} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                        <Alert variant="filled" onClose={handleCloseSnackbar} severity="success">
                            Logged in successfully!
                        </Alert>
                    </Snackbar>
                </Box>
            </Box>
        </>
    );
};

export default SignIn;
